import dayjs from 'dayjs'
import {
  AllCausal,
  AvailableOnlyCausal,
  Causal,
  CheckValueForAvailability,
  DateFormat,
  DateHeader,
  DocumentationNumber,
  EmptyFilterChecked,
  ProgressiveAvailability,
  Quantity,
  TimeFormat,
} from '../Constants/Configuration'
import { TableSortingDescend } from 'Constants/TableConfiguration'
import { unsensitiveCaseSearchMatch } from './StringHelpers'

const getSearchedTableFiltersValue = (tableColumns, tableFilters, negateTableColumns, priceFilter, allMarketplace) => {
  const searched = []
  for (const group in tableColumns) {
    for (const filter in tableColumns[group]) {
      if (
        tableFilters[tableColumns[group][filter].value] &&
        tableFilters[tableColumns[group][filter].value] !== EmptyFilterChecked &&
        negateTableColumns.filter(element => element === tableColumns[group][filter].value).length === 0
      ) {
        searched.push(tableColumns[group][filter].value)
      }
    }
  }
  if (Object.keys(priceFilter).length !== 0) {
    searched.push({ field: priceFilter.field, value: priceFilter.min + ' - ' + priceFilter.max })
  }
  for (const mkt in allMarketplace) {
    if (tableFilters[allMarketplace[mkt].column]) {
      searched.push(allMarketplace[mkt].column)
    }
  }
  return searched
}

const updateEmptyColumnTableFiltersValue = (emptyTableColumns, tableFilters, removedFilter?: string | null) => {
  for (const group in emptyTableColumns) {
    emptyTableColumns[group].forEach(filter => {
      if (removedFilter && filter.value === removedFilter) {
        filter.active = false
        delete tableFilters[filter.value]
        return
      }

      if (filter.active) {
        tableFilters[filter.value] = EmptyFilterChecked
      } else {
        if (tableFilters[filter.value] === EmptyFilterChecked) {
          delete tableFilters[filter.value]
        }
      }
    })
  }
}

const retrieveActiveEmptyColumns = (emptyTableColumns, emptyExtraDescriptions) => {
  const empty = []
  const emptyTableColumnsKeys = Object.keys(emptyTableColumns)
  emptyTableColumnsKeys.forEach(key => {
    emptyTableColumns[key].forEach(element => {
      if (element.active) {
        empty.push(element.value)
      }
    })
  })
  emptyExtraDescriptions.forEach(element => {
    empty.push({ field: element.field, languageCode: element.languageCode })
  })

  return empty
}

const orderAndSetMarketplaceFilterFromData = allMarketplace => {
  const siteMrkp = allMarketplace
    .filter(x => checkSiteMarketplace(x.column ? x.column : x.name))
    .map((marketplace, index) => {
      const mktName = marketplace.alias ? marketplace.alias : marketplace.name
      return { value: mktName, active: false, visible: marketplace.enabled }
    })
    .sort((a, b) => localeCompareValues(a.value, b.value))
  const oldMrkp = allMarketplace
    .filter(x => !checkSiteMarketplace(x.column ? x.column : x.name) && !checkNewMarketplace(x.column))
    .map((marketplace, index) => {
      const mktName = marketplace.alias ? marketplace.alias : marketplace.name
      return { value: mktName, active: false, visible: marketplace.enabled }
    })
    .sort((a, b) => localeCompareValues(a.value, b.value))
  const newMrkp = allMarketplace
    .filter(x => checkNewMarketplace(x.column))
    .map((marketplace, index) => {
      const mktName = marketplace.alias ? marketplace.alias : marketplace.name
      return { value: mktName, active: false, visible: marketplace.enabled }
    })
    .sort((a, b) => localeCompareValues(a.value, b.value))

  return [...siteMrkp, ...oldMrkp, ...newMrkp]
}

const orderAndSetMarketplaceDefaultColumnFromData = mktAlias => {
  const siteMrkp = mktAlias
    .filter(x => checkSiteMarketplace(x.column ? x.column : x.name))
    .map((marketplace, index) => {
      const mktName = marketplace.alias ? marketplace.alias : marketplace.name
      return {
        name: mktName,
        isMarketplace: true,
        mktColumn: marketplace.column ? marketplace.column.toUpperCase() : '',
      }
    })
    .sort((a, b) => localeCompareValues(a.value, b.value))
  const oldMrkp = mktAlias
    .filter(x => !checkSiteMarketplace(x.column ? x.column : x.name) && !checkNewMarketplace(x.column))
    .map((marketplace, index) => {
      const mktName = marketplace.alias ? marketplace.alias : marketplace.name
      return {
        name: mktName,
        isMarketplace: true,
        mktColumn: marketplace.column ? marketplace.column.toUpperCase() : '',
      }
    })
    .sort((a, b) => localeCompareValues(a.value, b.value))
  const newMrkp = mktAlias
    .filter(x => checkNewMarketplace(x.column))
    .map((marketplace, index) => {
      const mktName = marketplace.alias ? marketplace.alias : marketplace.name
      return {
        name: mktName,
        isMarketplace: true,
        mktColumn: marketplace.column ? marketplace.column.toUpperCase() : '',
      }
    })
    .sort((a, b) => localeCompareValues(a.value, b.value))

  return [...siteMrkp, ...oldMrkp, ...newMrkp]
}

const checkSiteMarketplace = mktName => {
  return mktName ? mktName.toLowerCase().startsWith('sito') : false
}

const checkNewMarketplace = mktName => {
  return mktName ? mktName.toLowerCase().startsWith('mrkp') : false
}

const localeCompareValues = (a, b) => {
  return a ? a.localeCompare(b) : a === b ? 0 : -1
}

const getActiveFilterValue = array => {
  if (!array) return undefined
  return array.map(filter => (filter.active ? filter.value : null)).filter(x => x)
}

const customSortTableData = (data, sortInfo) => {
  const sortInfoKeys = Object.keys(sortInfo).length > 0
  if (!sortInfoKeys) {
    return data
  }

  const resultSorted = data.sort((a, b) => {
    if (sortInfo.columnKey) {
      switch (sortInfo.columnKey) {
        case Causal:
          return a[sortInfo.columnKey]?.localeCompare(b[sortInfo.columnKey])
        case Quantity:
          return a[sortInfo.columnKey] - b[sortInfo.columnKey]
        case DateHeader:
          const firstDate = dayjs(a[sortInfo.columnKey], DateFormat + ' ' + TimeFormat)
          const secondDate = dayjs(b[sortInfo.columnKey], DateFormat + ' ' + TimeFormat)
          if (firstDate.isBefore(secondDate)) {
            return -1
          } else {
            return 1
          }
        case ProgressiveAvailability:
          return a[sortInfo.columnKey] - b[sortInfo.columnKey]
        case DocumentationNumber:
          return a[sortInfo.columnKey]?.localeCompare(b[sortInfo.columnKey], undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        case CheckValueForAvailability:
          return a[sortInfo.columnKey] - b[sortInfo.columnKey]
        default:
          const typeCheck = typeof a[sortInfo.columnKey]
          if (typeCheck === 'string') {
            return a[sortInfo.columnKey]?.localeCompare(b[sortInfo.columnKey])
          }
          if (typeCheck === 'number') {
            return a[sortInfo.columnKey] - b[sortInfo.columnKey]
          }
          return 0
      }
    } else {
      return 0
    }
  })

  if (sortInfo.order && sortInfo.order === TableSortingDescend) {
    return resultSorted.reverse()
  }

  return resultSorted
}

const filterDataByCausalAndStore = (data, causal, searchValue) => {
  if (causal !== AllCausal && causal !== AvailableOnlyCausal) {
    data = data.filter(element => {
      return unsensitiveCaseSearchMatch(element.causal, causal)
    })
  }

  if (!searchValue || searchValue.length < 2) return data

  return data.filter(element => {
    return unsensitiveCaseSearchMatch(element.storeName, searchValue)
  })
}

const addHoverClassToTag = label => {
  const tagElements = Array.from(document.getElementsByClassName('searched-tag ' + label))

  tagElements?.forEach(element => {
    element.classList.add('hover')
  })
}

const removeHoverClassFromTag = label => {
  const tagElements = Array.from(document.getElementsByClassName('searched-tag ' + label))

  tagElements?.forEach(element => {
    element.classList.remove('hover')
  })
}

export {
  getSearchedTableFiltersValue,
  updateEmptyColumnTableFiltersValue,
  retrieveActiveEmptyColumns,
  orderAndSetMarketplaceFilterFromData,
  orderAndSetMarketplaceDefaultColumnFromData,
  getActiveFilterValue,
  customSortTableData,
  filterDataByCausalAndStore,
  addHoverClassToTag,
  removeHoverClassFromTag,
}
